import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.classList.contains('media-gallery--hover-zoom')) {
      this.#setupHoverZoom(signal);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #setupHoverZoom(signal) {
    const containers = this.querySelectorAll('.product-media-container--image .product-media');
    const move = (e) => {
      const el = /** @type {HTMLElement} */ (e.currentTarget);
      const img = el.querySelector('.product-media__image');
      if (!img) return;
      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      const xPct = `${(x * 100).toFixed(2)}%`;
      const yPct = `${(y * 100).toFixed(2)}%`;
      img.style.setProperty('--hover-origin-x', xPct);
      img.style.setProperty('--hover-origin-y', yPct);
      img.style.transformOrigin = `${xPct} ${yPct}`;
    };

    const leave = (e) => {
      const el = /** @type {HTMLElement} */ (e.currentTarget);
      const img = el.querySelector('.product-media__image');
      if (!img) return;
      img.style.removeProperty('--hover-origin-x');
      img.style.removeProperty('--hover-origin-y');
      img.style.transformOrigin = '';
    };

    containers.forEach((c) => {
      c.addEventListener('pointermove', move, { signal });
      c.addEventListener('pointerleave', leave, { signal });
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
